// Story ▸ Timeline (StoryRoom.swift's `StoryTimelinePage`, `CalendarLanes`, `SceneSummary`; StoryCharts.swift's
// `StoryTimelineView`): by story time, a numbered lane for each of the (at most ten) most present characters across the
// days of the story, a dot for each scene they're in — filled for the point of view, faded when the time is estimated —
// and a line joining everyone in the same scene; choosing a dot shows that scene, who's in it and Open in Write. By
// reading order: story time against the order of telling, character lanes, and objects and motifs, on one x axis (the
// scene's place in the arrangement) with their labels in a gutter, so a column is one scene in every chart.
import { useRef, useState } from 'react';
import type { Project } from '@writing-suite/core';
import { readingOrderLanes, sceneTime, timeVersusOrder, type LaneMark, type StoryScene } from '@writing-suite/core/story';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { useWorkspace } from '../../shell/workspace.ts';
import { calendarLanesOf, opened, setStoryPrefs, storyModel, storyScenesOf, useStoryPrefs } from '../../store/story.ts';
import { arrowStep, dateTicks, evenTicks, invert, scale, spanLabel, useWidth } from './charts.ts';
import { EntityDot, StoryEmpty, storyDate, useStoryEmphasis } from './common.tsx';
import { entityCSSColor } from '@writing-suite/core';
import { StoryTimeSheet } from './StoryTimeSheet.tsx';

export function TimelinePage(props: PageProps) {
  const { project, arrangement } = props;
  const prefs = useStoryPrefs();
  const scenes = storyScenesOf(project, arrangement);
  return (
    <div className="story-page timeline-page" data-timeline={prefs.byStoryTime ? 'story-time' : 'reading-order'}>
      <header className="page-head">
        <h1 tabIndex={-1}>Timeline</h1>
        <p className="page-sub">{prefs.byStoryTime ? 'By story time' : 'By reading order'}</p>
      </header>
      {prefs.byStoryTime ? <CalendarLanesView project={project} scenes={scenes} /> : <ReadingOrder project={project} scenes={scenes} />}
      {prefs.suggesting ? <StoryTimeSheet project={project} live={props.live} onClose={() => setStoryPrefs({ suggesting: false })} /> : null}
    </div>
  );
}

/** The Timeline's header: by story time or reading order, and Suggest Story Times. */
export function TimelineControls() {
  const prefs = useStoryPrefs();
  return (
    <>
      <div className="segmented small" role="radiogroup" aria-label="Order">
        <button type="button" role="radio" aria-checked={prefs.byStoryTime} className={prefs.byStoryTime ? 'on' : ''} onClick={() => setStoryPrefs({ byStoryTime: true })} title="Lay the scenes out by when they happen">
          By story time
        </button>
        <button type="button" role="radio" aria-checked={!prefs.byStoryTime} className={prefs.byStoryTime ? '' : 'on'} onClick={() => setStoryPrefs({ byStoryTime: false })} title="Lay the scenes out in the order they’re told">
          By reading order
        </button>
      </div>
      <SuggestButton />
    </>
  );
}

export function SuggestButton() {
  return (
    <button type="button" className="quiet-button" title="Suggest story times from dates and transitions in the scenes’ openings" onClick={() => setStoryPrefs({ suggesting: true })}>
      Suggest Story Times…
    </button>
  );
}

// MARK: Calendar lanes

const LANE = 72;
const TOP = 34;

function CalendarLanesView({ project, scenes }: { project: Project; scenes: StoryScene[] }) {
  const ws = useWorkspace();
  const { emphasis, faded } = useStoryEmphasis();
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const [selected, setSelected] = useState<string | null>(null);
  const data = calendarLanesOf(project, scenes);
  const entities = opened(project).record.entities;
  const entity = (id: string) => entities.find((e) => e.id === id);
  if (!data.scenes.length || !data.lanes.length) {
    return (
      <StoryEmpty>
        Scenes with a story time and the characters in them appear here, a lane for each character. Choose Suggest Story Times to fill times in from the text, and type @ in
        Write to name the people in a scene.
      </StoryEmpty>
    );
  }
  const current = data.scenes.find((s) => s.id === selected) ?? data.scenes.find((s) => s.id === ws?.caretFragment) ?? data.marks[0]?.scene ?? data.scenes[0];
  const label = width < 520 ? 96 : 130;
  const times = data.scenes.map((s) => sceneTime(s)!);
  const min = Math.min(...times), max = Math.max(...times);
  const x0 = label + 14, x1 = Math.max(x0 + 40, width - 18);
  const x = scale(min, max, x0, x1);
  const height = TOP + data.lanes.length * LANE + 8;
  const yOf = (lane: string) => TOP + (data.lanes.indexOf(lane) + 0.5) * LANE;
  const axis = dateTicks(min, max, x1 - x0);
  const present = (s: StoryScene) => data.lanes.filter((l) => data.marks.some((m) => m.scene.id === s.id && m.lane === l));
  const pick = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left, py = e.clientY - r.top;
    const when = invert(min, max, x0, x1)(px);
    const index = Math.floor((py - TOP) / LANE);
    const lane = index >= 0 && index < data.lanes.length ? data.lanes[index] : null;
    const candidates = data.marks.filter((m) => lane === null || m.lane === lane).map((m) => m.scene);
    const pool = candidates.length ? candidates : data.scenes;
    const best = pool.reduce((a, b) => (Math.abs(sceneTime(b)! - when) < Math.abs(sceneTime(a)! - when) ? b : a));
    setSelected(best.id);
  };
  return (
    <>
      <div className="chart-box" ref={box}>
        <svg
          className="story-chart calendar-lanes"
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="group"
          aria-label="Characters across the days of the story. Use the arrow keys to choose a scene."
          tabIndex={0}
          onPointerUp={pick}
          onKeyDown={(e) => {
            const next = arrowStep(e, data.scenes, current);
            if (next) {
              e.preventDefault();
              setSelected(next.id);
            }
          }}
        >
          {axis.ticks.map((t) => (
            <g key={t} className="axis-tick">
              <line className="grid-soft" x1={x(t)} x2={x(t)} y1={TOP - 6} y2={height - 8} />
              <text className="axis-label" x={x(t)} y={TOP - 14} textAnchor="middle">
                {axis.label(t)}
              </text>
            </g>
          ))}
          {data.lanes.map((lane, i) => {
            const e = entity(lane);
            const y = TOP + (i + 0.5) * LANE;
            return (
              <g key={lane} className="story-lane" data-lane={lane}>
                <line className="grid" x1={x0 - 8} x2={x1 + 8} y1={y} y2={y} />
                <circle cx={10} cy={y} r={4} fill={entityCSSColor(e?.color)} />
                <text className="lane-label" x={20} y={y + 5}>
                  {truncate(e?.name ?? '', label < 120 ? 11 : 15)}
                </text>
              </g>
            );
          })}
          {data.links.map((l) => (
            <line
              key={l.id}
              className={(l.scene.id === current?.id ? 'link current' : 'link') + faded(l.scene.id)}
              x1={x(sceneTime(l.scene)!)}
              x2={x(sceneTime(l.scene)!)}
              y1={yOf(l.top)}
              y2={yOf(l.bottom)}
            />
          ))}
          {data.marks.map((m) => {
            const color = entityCSSColor(entity(m.lane)?.color);
            const chosen = m.scene.id === current?.id;
            return (
              <circle
                key={m.id}
                className={`dot${chosen ? ' chosen' : ''}${m.pov || chosen ? '' : ' hollow'}`}
                data-scene-id={m.scene.id}
                data-pov={m.pov || undefined}
                cx={x(sceneTime(m.scene)!)}
                cy={yOf(m.lane)}
                r={chosen ? 8 : 6}
                fill={m.pov || chosen ? color : undefined}
                stroke={color}
                strokeWidth={2}
                opacity={(m.scene.storyTime === null ? 0.5 : 1) * emphasis(m.scene.id)}
              >
                <title>{`${entity(m.lane)?.name ?? ''}, ${m.scene.title}`}</title>
              </circle>
            );
          })}
        </svg>
      </div>
      <p className="chart-note">A filled dot is the point of view; a line joins the people in the same scene. Faded dots are estimated times.</p>
      {current ? <SceneSummary project={project} scene={current} present={present(current)} /> : null}
    </>
  );
}

const truncate = (s: string, n: number) => (Array.from(s).length > n ? Array.from(s).slice(0, n - 1).join('') + '…' : s);

/** The chosen scene under the timeline: when, what, who, and a way into it. */
function SceneSummary({ project, scene, present }: { project: Project; scene: StoryScene; present: string[] }) {
  const ws = useWorkspace();
  const entities = opened(project).record.entities;
  const synopsis = project.fragment(scene.id)?.synopsis ?? '';
  const time = sceneTime(scene);
  const names = present.map((id) => entities.find((e) => e.id === id)?.name).filter(Boolean).join(', ');
  return (
    <section className="scene-summary" aria-label="The chosen scene" data-scene-id={scene.id}>
      <div className="scene-summary-main">
        <p className="eyebrow">
          Scene {scene.position}
          {time !== null ? ` · ${storyDate(time)}` : ''}
          {scene.storyTime === null ? ' (estimated)' : ''}
        </p>
        <h2 className="scene-summary-title">{scene.title}</h2>
        {synopsis ? <p className="scene-summary-synopsis">{synopsis}</p> : null}
      </div>
      <div className="scene-summary-side">
        <p className="eyebrow">Together in this scene</p>
        <p className="scene-summary-people">{names || 'No one named'}</p>
        <button type="button" className="link-button" onClick={() => ws?.reveal({ fragmentId: scene.id })}>
          Open in Write →
        </button>
      </div>
    </section>
  );
}

// MARK: By reading order

const GUTTER = 120;

function ReadingOrder({ project, scenes }: { project: Project; scenes: StoryScene[] }) {
  const ws = useWorkspace();
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);
  const [selected, setSelected] = useState<string | null>(null);
  const chosen = scenes.find((s) => s.id === selected) ?? null;
  const gutter = width < 520 ? 88 : GUTTER;
  const x0 = gutter + 8, x1 = Math.max(x0 + 40, width - 14);
  const x = scale(0, Math.max(1, scenes.length + 1), x0, x1);
  const toPosition = invert(0, Math.max(1, scenes.length + 1), x0, x1);
  const common = { scenes, x, x0, x1, width, gutter, toPosition, selected: chosen, onSelect: (id: string | null) => setSelected(id) };
  const time = chosen ? sceneTime(chosen) : null;
  return (
    <div className="reading-order" ref={box}>
      {chosen ? (
        <div className="selected-scene-bar" role="status" data-scene-id={chosen.id}>
          <span className="selected-scene-title">
            Scene {chosen.position}: {chosen.title}
          </span>
          {time !== null ? <span className="selected-scene-time">{storyDate(time) + (chosen.storyTime === null ? ' (estimated)' : '')}</span> : null}
          <button type="button" className="quiet-button" onClick={() => ws?.reveal({ fragmentId: chosen.id })}>
            Open
          </button>
          <button type="button" className="quiet-button" aria-label="Clear" onClick={() => setSelected(null)}>
            ×
          </button>
        </div>
      ) : null}
      <section className="story-chart-section" aria-labelledby="chart-time">
        <h2 id="chart-time" className="eyebrow">Story time</h2>
        <TimeVsOrder project={project} {...common} />
      </section>
      <section className="story-chart-section" aria-labelledby="chart-characters">
        <h2 id="chart-characters" className="eyebrow">Characters</h2>
        <Lanes project={project} kinds={['character']} {...common} />
      </section>
      <section className="story-chart-section" aria-labelledby="chart-things">
        <h2 id="chart-things" className="eyebrow">Objects and motifs</h2>
        <Lanes project={project} kinds={['object', 'motif']} {...common} />
      </section>
    </div>
  );
}

interface AxisProps {
  scenes: StoryScene[];
  x: (position: number) => number;
  x0: number;
  x1: number;
  width: number;
  gutter: number;
  toPosition: (px: number) => number;
  selected: StoryScene | null;
  onSelect: (id: string | null) => void;
}

/** x = position in the arrangement, y = story time (later higher); one line per POV strand. Flashbacks dip, time jumps
 *  are cliffs. Estimated times are faded. */
function TimeVsOrder({ project, scenes, x, x0, x1, width, gutter, toPosition, selected, onSelect }: AxisProps & { project: Project }) {
  const { emphasis } = useStoryEmphasis();
  const entities = opened(project).record.entities;
  const points = timeVersusOrder(scenes, entities);
  if (!points.length) {
    return <StoryEmpty>Give scenes a story time (in the inspector’s Story section), or choose Suggest Story Times, to see how the telling moves through story time.</StoryEmpty>;
  }
  const H = 240, top = 12, bottom = H - 14;
  const times = points.map((p) => p.time);
  const min = Math.min(...times), max = Math.max(...times);
  const y = scale(min, max, bottom, top);
  const ticks = evenTicks(times);
  const strands = [...new Set(points.map((p) => p.strand))];
  const colorOf = (strand: string) => {
    const e = entities.find((x) => x.name === strand);
    return e ? entityCSSColor(e.color) : null;
  };
  return (
    <>
      <ul className="chart-legend" aria-label="Point of view">
        {strands.map((s) => (
          <li key={s}>
            {colorOf(s) ? <EntityDot color={entities.find((e) => e.name === s)?.color} /> : <span className="entity-dot none" aria-hidden="true" />}
            {s}
          </li>
        ))}
      </ul>
      <svg
        className="story-chart time-order"
        width={width}
        height={H}
        viewBox={`0 0 ${width} ${H}`}
        role="group"
        aria-label="Story time against the order of telling. Use the arrow keys to choose a scene."
        tabIndex={0}
        onPointerUp={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const pos = toPosition(e.clientX - r.left);
          const best = points.reduce((a, b) => (Math.abs(b.position - pos) < Math.abs(a.position - pos) ? b : a));
          onSelect(best.sceneID);
        }}
        onKeyDown={(e) => {
          const timed = scenes.filter((s) => points.some((p) => p.sceneID === s.id));
          const next = arrowStep(e, timed, selected);
          if (next) {
            e.preventDefault();
            onSelect(next.id);
          }
        }}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid-soft" x1={x0} x2={x1} y1={y(t)} y2={y(t)} />
            <text className="gutter-label" x={gutter - 4} y={y(t) + 4} textAnchor="end">
              {spanLabel(t, max - min)}
            </text>
          </g>
        ))}
        {strands.map((s) => {
          const line = points.filter((p) => p.strand === s).sort((a, b) => a.position - b.position);
          return (
            <polyline key={s} className={colorOf(s) ? 'strand' : 'strand none'} fill="none" stroke={colorOf(s) ?? undefined} points={line.map((p) => `${x(p.position)},${y(p.time)}`).join(' ')} />
          );
        })}
        {points.map((p) => (
          <circle key={p.sceneID} className={colorOf(p.strand) ? 'point' : 'point none'} data-scene-id={p.sceneID} cx={x(p.position)} cy={y(p.time)} r={p.estimated ? 2.5 : 4} fill={colorOf(p.strand) ?? undefined} opacity={(p.estimated ? 0.45 : 1) * emphasis(p.sceneID)}>
            <title>{scenes.find((s) => s.id === p.sceneID)?.title ?? ''}</title>
          </circle>
        ))}
        {selected ? <line className="selected-rule" x1={x(selected.position)} x2={x(selected.position)} y1={top - 6} y2={bottom + 6} /> : null}
      </svg>
      <p className="chart-note">Faded points are estimated between scenes with story times. Choose a point to mark the scene in every chart.</p>
    </>
  );
}

/** One row per character (or object and motif): each scene marked POV, on stage or mentioned; long absences shaded. */
function Lanes({ project, kinds, scenes, x, x0, x1, width, gutter, toPosition, selected, onSelect }: AxisProps & { project: Project; kinds: ('character' | 'object' | 'motif')[] }) {
  const { faded } = useStoryEmphasis();
  const entities = opened(project).record.entities;
  const { marks, names, gaps } = readingOrderLanes(scenes, storyModel(project).rows, entities, kinds);
  if (!marks.length) {
    return (
      <StoryEmpty>
        {kinds.includes('character') ? 'Add characters in Cast & Places. Scenes that name them appear here.' : 'Add objects or motifs in Cast & Places to follow them through the book.'}
      </StoryEmpty>
    );
  }
  const ROW = 36, top = 8;
  const H = top + Math.max(3, names.length) * ROW + 8;
  const yOf = (name: string) => top + (names.indexOf(name) + 0.5) * ROW;
  const symbol = (m: LaneMark) => {
    const cx = x(m.position), cy = yOf(m.entity);
    if (m.level === 'pov') return <polygon key={m.id} className={'mark pov' + faded(m.sceneID)} data-scene-id={m.sceneID} points={`${cx},${cy - 7} ${cx + 7},${cy} ${cx},${cy + 7} ${cx - 7},${cy}`}><title>{`${m.entity}: POV`}</title></polygon>;
    return (
      <circle key={m.id} className={`mark ${m.level}${faded(m.sceneID)}`} data-scene-id={m.sceneID} cx={cx} cy={cy} r={m.level === 'mentioned' ? 3 : 5.5}>
        <title>{`${m.entity}: ${m.level === 'mentioned' ? 'mentioned' : 'on stage'}`}</title>
      </circle>
    );
  };
  return (
    <>
      <ul className="chart-legend" aria-label="Presence">
        <li><span className="legend-mark pov" aria-hidden="true" />POV</li>
        <li><span className="legend-mark on-stage" aria-hidden="true" />On stage</li>
        <li><span className="legend-mark mentioned" aria-hidden="true" />Mentioned</li>
      </ul>
      <svg
        className="story-chart lanes"
        width={width}
        height={H}
        viewBox={`0 0 ${width} ${H}`}
        role="group"
        aria-label={`${kinds.includes('character') ? 'Characters' : 'Objects and motifs'} in reading order. Use the arrow keys to choose a scene.`}
        tabIndex={0}
        onPointerUp={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const pos = toPosition(e.clientX - r.left);
          const row = Math.floor((e.clientY - r.top - top) / ROW);
          const name = row >= 0 && row < names.length ? names[row] : null;
          const pool = marks.filter((m) => name === null || m.entity === name);
          const best = (pool.length ? pool : marks).reduce((a, b) => (Math.abs(b.position - pos) < Math.abs(a.position - pos) ? b : a));
          onSelect(best.sceneID);
        }}
        onKeyDown={(e) => {
          const next = arrowStep(e, scenes, selected);
          if (next) {
            e.preventDefault();
            onSelect(next.id);
          }
        }}
      >
        {gaps.map((g) => (
          <rect key={g.id} className="gap" x={x(g.start + 0.5)} width={Math.max(0, x(g.end - 0.5) - x(g.start + 0.5))} y={yOf(g.entity) - ROW * 0.35} height={ROW * 0.7} />
        ))}
        {names.map((n) => (
          <g key={n}>
            <line className="grid-soft" x1={x0} x2={x1} y1={yOf(n)} y2={yOf(n)} />
            <text className="gutter-label" x={gutter - 4} y={yOf(n) + 4} textAnchor="end">
              {truncate(n, gutter < 100 ? 11 : 16)}
            </text>
          </g>
        ))}
        {marks.map(symbol)}
        {selected ? <line className="selected-rule" x1={x(selected.position)} x2={x(selected.position)} y1={2} y2={H - 2} /> : null}
      </svg>
      <p className="chart-note">{kinds.includes('character') ? 'Shaded: absent for 10 scenes or more.' : 'Each mark is a scene where it appears.'}</p>
    </>
  );
}
